<template>
  <main class="l-main p-results-check">
    <TitleMain
      icon="sitekeywordanalysis"
      faq="/sections/360001240055"
      howTo="/sections/4404631665295"
    >
      <span class="title">成果チェック</span>
      検索順位の推移やクリック率などの成果データを分析し、<br />改善に取り組むべきキーワードを見つけましょう。
    </TitleMain>
    <div class="l-container">
      <AiWritingBanner :projectId="project.id" />
      <div class="l-toolBlock">
        <ButtonToProjects />
        <GaTypeStatus v-if="!isGaTypeError" gaType="ua" :project="project" />
        <Breadcrumb />
      </div>
      <div class="l-block">
        <Tab>
          <TabItem
            v-for="item in devices"
            :key="item.id"
            :id="item.id"
            :active="device"
            @click="updateKeywordDevice"
          >
            <Icon2 :variant="item.id" />
            {{ item.label }}
          </TabItem>
        </Tab>
        <div class="l-block__content">
          <div class="l-block__header">
            <h2 class="c-title c-title--h03">
              <Icon variant="resultscheck" :size="28" radius />
              <span class="title">すべてのキーワード成果</span>
            </h2>
            <div v-if="usage.max" class="c-list--usage__content">
              <span
                class="c-list--usage__current"
                :class="{ 'is-over': isUsageOver }"
                >{{ usage.current }}</span
              ><span>/</span
              ><span class="c-list--usage__max">{{ usage.max }}</span>
            </div>
            <ButtonRectangle
              iconLeft="plus"
              priority="high"
              size="large"
              @click="openModal"
            >
              キーワード登録
            </ButtonRectangle>
            <router-link
              :to="{
                name: 'configProject',
                params: { projectId: project.id },
                hash: '#siteImportantKeyword'
              }"
              class="p-results-check__config"
            >
              <Icon variant="gear_gy" :size="14" />確認・変更
            </router-link>
          </div>
          <Tab variant="inner">
            <TabItem
              id="siteImportantKeywordActions"
              :active="$route.name"
              :to="{
                name: 'siteImportantKeywordActions'
              }"
            >
              施策の成果
            </TabItem>
            <TabItem
              id="siteImportantKeywordRanks"
              :active="$route.name"
              :to="{
                name: 'siteImportantKeywordRanks'
              }"
            >
              検索順位チェック
            </TabItem>
          </Tab>
          <router-view
            gaType="ua"
            :device="device"
            :id="id"
            :keyword="keyword"
            :keywords="rankCheckKeywords"
            :project="project"
            :status="status"
            :usage="usage"
            @openModalAddKeyword="openModal"
          />
        </div>
      </div>
    </div>
    <portal-target class="c-form-task__wrapper" name="form-add-tasks" />
    <ModalAddKeyword
      v-if="isOpenModal"
      :items="rankCheckKeywords.items"
      :keyword="keyword"
      :project="project"
      :isLoading="rankCheckKeywords.isLoading"
      @add="addKeywords"
      @close="closeModal"
    />
  </main>
</template>

<script>
import AiWritingBanner from '@/components/aiWriting/AiWritingBanner';
import Breadcrumb from '@/components/Breadcrumb';
import ButtonRectangle from '@/components/ButtonRectangle';
import ButtonToProjects from '@/components/ButtonToProjects';
import GaTypeStatus from '@/components/GaTypeStatus';
import Icon from '@/components/Icon';
import Icon2 from '@/components/Icon2';
import ModalAddKeyword from '@/components/siteImportantKeywordGA4/ModalAddKeyword';
import Tab from '@/components/Tab';
import TabItem from '@/components/TabItem';
import TitleMain from '@/components/TitleMain';
import axios from 'axios';
import apiProjects from '@/api/projects';

export default {
  props: {
    id: {
      type: String,
      required: false
    },
    keyword: {
      type: String,
      required: false
    },
    project: {
      type: Object,
      required: true
    },
    status: {
      type: String,
      required: false
    },
    isGaTypeError: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    AiWritingBanner,
    Breadcrumb,
    ButtonRectangle,
    ButtonToProjects,
    GaTypeStatus,
    Icon,
    Icon2,
    ModalAddKeyword,
    Tab,
    TabItem,
    TitleMain
  },
  data() {
    return {
      addedKeywords: [],
      cancelToken: {
        rankCheckKeywords: null,
        usage: null
      },
      devices: [
        {
          id: 'desktop',
          label: 'PC'
        },
        {
          id: 'mobile',
          label: 'スマホ'
        }
      ],
      device: 'desktop',
      rankCheckKeywords: {
        isLoading: true,
        items: []
      },
      usage: {
        current: null,
        max: null
      },
      isOpenModal: this.status === 'add'
    };
  },
  computed: {
    isUsageOver() {
      return this.usage.current >= this.usage.max;
    }
  },
  created() {
    this.getUsage();
    this.$store.dispatch('task/getTaskSolutions');
    this.getRankCheckKeywords();
  },
  beforeDestroy() {
    if (this.cancelToken.usage) {
      this.cancelToken.usage.cancel();
    }
    if (this.cancelToken.rankCheckKeywords) {
      this.cancelToken.rankCheckKeywords.cancel();
    }
  },
  methods: {
    getRankCheckKeywords() {
      this.cancelToken.rankCheckKeywords = axios.CancelToken.source();

      apiProjects.observationKeywords
        .get(
          this.auth.idToken,
          this.project.id,
          this.cancelToken.rankCheckKeywords.token
        )
        .then(response => {
          const items = response.data.items;
          this.rankCheckKeywords.items = items;
          this.rankCheckKeywords.isLoading = false;
        })
        .catch(error => {
          this.rankCheckKeywords.isLoading = false;
        });
    },
    getUsage() {
      const params = {
        items: 'observation-keywords'
      };

      this.cancelToken.usage = axios.CancelToken.source();

      apiProjects.usages
        .get(
          params,
          this.auth.idToken,
          this.project.id,
          this.cancelToken.usage.token
        )
        .then(response => {
          this.usage.current = response.data.items[0].current;
          this.usage.max = response.data.items[0].max;
        });
    },
    updateKeywordDevice(id) {
      this.device = id;
    },
    addKeywords(items) {
      this.closeModal();
      this.getUsage();

      items = items.map(item => {
        item.isLoading = true;
        item.isNew = true;
        return item;
      });
      this.rankCheckKeywords.items = this.rankCheckKeywords.items.concat(items);
    },
    openModal() {
      this.isOpenModal = true;
    },
    closeModal() {
      this.isOpenModal = false;
    }
  }
};
</script>
